import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import type { PanelGrantCreditsResultDto, ReferralCodeStatus } from '@icebreaker/shared';
import { Tag } from '@icebreaker/ui';
import {
  REFERRAL_LEAD_STATUS_VARIANT,
  referralStatusLabel,
  referralStatusReason,
  type LeadReferral,
} from '@/features/sales/model/referral-status';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import type { GiftRejection } from '../model/grant-body';
import styles from './organizations.module.css';

/**
 * HEDİYE KODU BLOĞU (P6r). Çerçeveli kutu, üstte "HEDİYE KODU" etiketi.
 * Adaydan açılışta üç hâl çizilir:
 *  · geçerli kod  → kod · "+N hediye kredi" · Aktif rozeti + "Hediyeyi ekle" kutusu
 *  · geçersiz kod → kod + durum rozeti + gerekçe cümlesi (kutu ÇİZİLMEZ)
 *  · kod yok      → "Adayın formunda hediye kodu yok."
 * Kurum detayından açılışta kutunun içeriği diyaloğun kendi kod alanıdır
 * (`children`): alan hatası diyaloğun `useFormErrors` sözleşmesinde kalır.
 *
 * Yükleme anında kod reddedilirse (`blocked`) geçerli kodun rozeti reddin
 * nedenine döner ve kutu kapalı çizilir (P6r "Çatışma").
 */
export function GiftBlock({ children }: { children: ReactNode }) {
  const t = useTranslation();
  return (
    <section className={styles.giftBlock} aria-labelledby="grant-gift-title">
      <p id="grant-gift-title" className={styles.giftKicker}>
        {t.t('admin.credit.grantDialog.giftBlock')}
      </p>
      {children}
    </section>
  );
}

export function LeadGiftContent({
  lead,
  organizationName,
  giftOn,
  disabled,
  locked,
  blocked,
  onToggle,
}: {
  lead: LeadReferral;
  organizationName: string;
  giftOn: boolean;
  disabled: boolean;
  /** "Yalnız hediye" seçili: hediye yüklemenin kendisidir, kutu işaretli ve kilitli (P6r soluklaştırmaz). */
  locked: boolean;
  blocked: GiftRejection | null;
  onToggle: (next: boolean) => void;
}) {
  const t = useTranslation();
  const code = lead.referralCode;
  const status = lead.referralCodeStatus;
  if (code === null || status === null) {
    return <p className={styles.giftNone}>{t.t('admin.credit.grantDialog.giftNoCode')}</p>;
  }
  if (status !== 'valid') {
    return (
      <>
        <p className={styles.giftRow}>
          <b className={[styles.giftCode, styles.giftCodeMuted].join(' ')}>{code}</b>
          <Tag variant={REFERRAL_LEAD_STATUS_VARIANT[status]}>
            {referralStatusLabel(t, status, lead.referralCodeUsedAt)}
          </Tag>
        </p>
        <p className={styles.giftWhy}>{referralStatusReason(t, lead, organizationName)}</p>
      </>
    );
  }
  // Reddin adları adaydaki durum adlarının alt kümesidir: rozet aynı tablodan okunur.
  const shown: ReferralCodeStatus = blocked ?? 'valid';
  const gift = lead.referralCodeGiftCredits;
  return (
    <>
      <p className={styles.giftRow}>
        <b className={styles.giftCode}>{code}</b>
        {gift !== null ? (
          <>
            <span className={styles.giftDot} aria-hidden="true">
              ·
            </span>
            <span className={styles.giftValue}>
              {t.t('admin.credit.grantDialog.giftValue', { count: gift })}
            </span>
          </>
        ) : null}
        <Tag variant={REFERRAL_LEAD_STATUS_VARIANT[shown]}>
          {referralStatusLabel(t, shown, null)}
        </Tag>
      </p>
      <label
        className={[styles.giftCheck, locked && !disabled ? styles.giftCheckLocked : '']
          .filter(Boolean)
          .join(' ')}
      >
        <span className={styles.giftBox}>
          <input
            type="checkbox"
            className={styles.giftCheckbox}
            checked={giftOn && !blocked}
            disabled={disabled || locked || blocked !== null}
            onChange={(event) => onToggle(event.target.checked)}
          />
          <Check size={13} strokeWidth={3} className={styles.giftCheckMark} aria-hidden="true" />
        </span>
        <span>{t.t('admin.credit.grantDialog.addGift')}</span>
      </label>
      <p className={styles.giftNote}>{t.t('admin.credit.grantDialog.giftFromLead')}</p>
    </>
  );
}

/**
 * BAŞARI ÖZETİ (P6r "Başarılı"): ödenen hak, hediye ve kod kullanımı ayrı
 * satırlarda. "Yalnız hediye" yüklemesinde üst düzey hak hediyenin kendisidir,
 * ödenen hak satırı yazılmaz.
 */
export function GrantSuccessLines({
  result,
  organizationName,
}: {
  result: PanelGrantCreditsResultDto;
  organizationName: string;
}) {
  const t = useTranslation();
  const gift = result.gift;
  const giftOnly = gift !== null && gift.grant.id === result.id;
  const lines: string[] = [];
  if (!giftOnly) {
    lines.push(
      result.kind === 'pack_12'
        ? t.t('admin.credit.grantDialog.successPaidPack', { count: result.amount })
        : t.t('admin.credit.grantDialog.successPaidSingle', { count: result.amount }),
    );
  }
  if (gift) {
    lines.push(
      t.t('admin.credit.grantDialog.successGift', {
        count: gift.grant.amount,
        code: gift.referralCode,
      }),
      t.t('admin.credit.grantDialog.successUsage', { org: organizationName }),
    );
  }
  return (
    <ul className={styles.success} role="status">
      {lines.map((line) => (
        <li key={line} className={styles.successLine}>
          <Check size={14} aria-hidden="true" />
          <span>{line}</span>
        </li>
      ))}
    </ul>
  );
}
